import { useState } from "react";

const Button = ({
    value,
    variant = "secondary",
    size = "md",
    width,
    minWidth,
    onClick,
    disabled = false,
    loading = false,
    iconLeft,
    iconRight,
    iconSize,
    className = "",
    style = {},
    type = "button",
    ...rest
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const sizes = {
        sm: {
            height: "32px",
            padding: "0 12px",
            fontSize: "var(--text-sm)",
            iconSize: "12px",
            minWidth: "64px",
        },
        md: {
            height: "40px",
            padding: "0 20px",
            fontSize: "var(--text-base)",
            iconSize: "14px",
            minWidth: "80px",
        },
        lg: {
            height: "48px",
            padding: "0 24px",
            fontSize: "var(--text-md)",
            iconSize: "16px",
            minWidth: "96px",
        },
    };

    const variants = {
        primary: {
            base: {
                backgroundColor: "var(--accent)",
                color: "var(--text-inverse)",
                border: "none",
            },
            hover: {
                backgroundColor: "var(--accent-hover)",
            },
        },
        secondary: {
            base: {
                backgroundColor: "var(--bg-tertiary)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-primary)",
            },
            hover: {
                backgroundColor: "var(--bg-secondary)",
                borderColor: "var(--text-tertiary)",
            },
        },
        ghost: {
            base: {
                backgroundColor: "transparent",
                color: "var(--text-secondary)",
                border: "1px solid transparent",
            },
            hover: {
                backgroundColor: "var(--bg-tertiary)",
            },
        },
        outline: {
            base: {
                backgroundColor: "transparent",
                color: "var(--accent)",
                border: "1px solid var(--accent)",
            },
            hover: {
                backgroundColor: "var(--accent-subtle)",
            },
        },
        danger: {
            base: {
                backgroundColor: "var(--error)",
                color: "var(--text-inverse)",
                border: "none",
            },
            hover: {
                backgroundColor: "#9B2C2C",
            },
        },
        "danger-outline": {
            base: {
                backgroundColor: "transparent",
                color: "var(--error)",
                border: "1px solid var(--error)",
            },
            hover: {
                backgroundColor: "var(--error-subtle)",
            },
        },
    };

    const currentSize = sizes[size] || sizes.md;
    const currentVariant = variants[variant] || variants.secondary;

    const baseStyles = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        height: currentSize.height,
        padding: currentSize.padding,
        fontSize: currentSize.fontSize,
        fontWeight: "var(--weight-medium)",
        fontFamily: "var(--font-primary)",
        lineHeight: 1,
        borderRadius: "var(--radius-md)",
        cursor: disabled || loading ? "not-allowed" : "pointer",
        transition: "all var(--transition-fast)",
        userSelect: "none",
        outline: "none",
        width: width || "auto",
        minWidth: minWidth || currentSize.minWidth,
        opacity: disabled ? 0.5 : 1,
        transform: isPressed && !disabled ? "scale(0.98)" : "scale(1)",
        ...currentVariant.base,
        ...(isHovered && !disabled && !loading ? currentVariant.hover : {}),
        ...style,
    };

    const handleClick = (e) => {
        if (disabled || loading) {
            e.preventDefault();
            return;
        }
        onClick?.(e);
    };

    const resolvedIconSize = iconSize || currentSize.iconSize;

    return (
        <button
            type={type}
            className={className}
            style={baseStyles}
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setIsPressed(false);
            }}
            onMouseDown={() => !disabled && !loading && setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            disabled={disabled || loading}
            {...rest}
        >
            {loading ? (
                <span
                    style={{
                        width: resolvedIconSize,
                        height: resolvedIconSize,
                        border: "2px solid transparent",
                        borderTopColor: "currentColor",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                    }}
                />
            ) : (
                iconLeft && (
                    <i
                        className={`fa ${iconLeft}`}
                        style={{ fontSize: resolvedIconSize }}
                        aria-hidden="true"
                    />
                )
            )}

            {value && <span>{value}</span>}

            {!loading && iconRight && (
                <i
                    className={`fa ${iconRight}`}
                    style={{ fontSize: resolvedIconSize }}
                    aria-hidden="true"
                />
            )}
        </button>
    );
};

export default Button;
